// cart.ts — module-level uniform-store cart, mirroring the `_token` singleton
// pattern in api.ts. Holds enriched line items (name/price/photo) so the cart and
// checkout render without re-fetching the catalog. Persisted to localStorage so a
// cart survives a Mini App reload. Components subscribe for reactive re-renders.

export interface CartItem {
  sku: string;
  nombre: string;
  talla: string;        // '' when the product has no sizes
  precioUnit: number;
  cantidad: number;
  fotoUrl: string;
}

const STORAGE_KEY = 'sophi:tienda:cart';
let _items: CartItem[] = load();
const listeners = new Set<() => void>();

function load(): CartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persist() {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(_items)); } catch { /* ignore quota/SSR */ }
}

function emit() {
  persist();
  listeners.forEach((l) => l());
}

const lineKey = (sku: string, talla: string) => `${sku}|${talla}`;

export function getCart(): CartItem[] { return _items.slice(); }
export function cartCount(): number { return _items.reduce((s, i) => s + i.cantidad, 0); }
export function cartTotal(): number { return _items.reduce((s, i) => s + i.precioUnit * i.cantidad, 0); }

export function subscribeCart(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Add `qty` units of (sku, talla); a matching line stacks its quantity. */
export function addToCart(item: Omit<CartItem, 'cantidad'>, qty = 1): void {
  const k = lineKey(item.sku, item.talla);
  const existing = _items.find((i) => lineKey(i.sku, i.talla) === k);
  if (existing) existing.cantidad += qty;
  else _items.push({ ...item, cantidad: qty });
  emit();
}

/** Set an exact quantity; 0 removes the line. */
export function setQty(sku: string, talla: string, qty: number): void {
  const k = lineKey(sku, talla);
  _items = _items
    .map((i) => (lineKey(i.sku, i.talla) === k ? { ...i, cantidad: Math.max(0, Math.floor(qty)) } : i))
    .filter((i) => i.cantidad > 0);
  emit();
}

export function removeFromCart(sku: string, talla: string): void {
  const k = lineKey(sku, talla);
  _items = _items.filter((i) => lineKey(i.sku, i.talla) !== k);
  emit();
}

export function clearCart(): void {
  _items = [];
  emit();
}

/** Shape POST /store/orders expects. */
export function cartLines(): { sku: string; talla: string; cantidad: number }[] {
  return _items.map((i) => ({ sku: i.sku, talla: i.talla, cantidad: i.cantidad }));
}
